/**
 * Build Your Set - Accessible Dropdown & Canvas System
 * WCAG 2.1 Level AA Compliant
 */

(function() {
  'use strict';

  // Define the class first
  class BuildYourSetSection {
    constructor(container) {
      this.container = container;
      this.sectionId = this.getSectionId();
      this.config = window.buildYourSetConfig?.[this.sectionId] || {};

      console.log('Build Your Set: Initializing section', this.sectionId);

      // Cache DOM elements
      this.dropdownButtons = this.container.querySelectorAll('.dropdown-button');
      this.addToCartButton = this.container.querySelector('.set-add-to-cart');
      this.totalPriceElement = this.container.querySelector(`#total-price-${this.sectionId}`);

      console.log('Build Your Set: Found', this.dropdownButtons.length, 'dropdown buttons');

      // State
      this.selections = new Map(); // layerId => { variantId, price, image, title }
      this.activeDropdown = null;

      // Initialize
      this.bindEvents();
      this.restoreSelections(); // Restore from sessionStorage
      this.updateAddToCartButton();
    }

    getSectionId() {
      // Extract section ID from any element with data-section-id or mah-section-{id} class
      const button = this.container.querySelector('[data-section-id]');
      if (button) return button.dataset.sectionId;

      const sectionDiv = this.container.closest('[class*="mah-section-"]');
      if (sectionDiv) {
        const match = sectionDiv.className.match(/mah-section-(\d+)/);
        if (match) return match[1];
      }

      return 'unknown';
    }

    bindEvents() {
      // Dropdown button clicks
      this.dropdownButtons.forEach(button => {
        button.addEventListener('click', (e) => this.toggleDropdown(e, button));
        button.addEventListener('keydown', (e) => this.handleButtonKeydown(e, button));
      });

      // Dropdown item clicks
      this.container.querySelectorAll('.dropdown-item').forEach(item => {
        item.addEventListener('click', (e) => this.selectItem(e, item));
        item.addEventListener('keydown', (e) => this.handleItemKeydown(e, item));
      });

      // Add to cart button
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', () => this.onSubmitHandler());
      }

      // Close dropdowns on outside click
      document.addEventListener('click', (e) => {
        if (!this.container.contains(e.target)) {
          this.closeAllDropdowns();
        }
      });

      // Close dropdowns on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeDropdown) {
          this.closeDropdown(this.activeDropdown);
          // Return focus to the button that opened it
          const button = this.activeDropdown.previousElementSibling;
          if (button && button.classList.contains('dropdown-button')) {
            button.focus();
          }
        }
      });
    }

    toggleDropdown(event, button) {
      event.stopPropagation();
      console.log('Build Your Set: Dropdown button clicked', button);

      const dropdown = button.nextElementSibling;
      console.log('Build Your Set: Dropdown element', dropdown);

      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      // Close all other dropdowns first
      this.closeAllDropdowns();

      if (!isExpanded) {
        this.openDropdown(button, dropdown);
      }
    }

    openDropdown(button, dropdown) {
      // Update ARIA states
      button.setAttribute('aria-expanded', 'true');
      dropdown.style.display = 'block';

      // Set active dropdown
      this.activeDropdown = dropdown;

      // Focus first item
      const firstItem = dropdown.querySelector('.dropdown-item');
      if (firstItem) {
        firstItem.setAttribute('tabindex', '0');
        firstItem.focus();
      }
    }

    closeDropdown(dropdown) {
      if (!dropdown) return;

      const button = dropdown.previousElementSibling;
      if (button && button.classList.contains('dropdown-button')) {
        button.setAttribute('aria-expanded', 'false');
      }

      dropdown.style.display = 'none';

      // Reset tabindex on all items
      dropdown.querySelectorAll('.dropdown-item').forEach(item => {
        item.setAttribute('tabindex', '-1');
      });

      if (this.activeDropdown === dropdown) {
        this.activeDropdown = null;
      }
    }

    closeAllDropdowns() {
      this.container.querySelectorAll('.dropdown-options').forEach(dropdown => {
        this.closeDropdown(dropdown);
      });
    }

    handleButtonKeydown(event, button) {
      const dropdown = button.nextElementSibling;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      switch(event.key) {
        case 'Enter':
        case ' ': // Spacebar
          event.preventDefault();
          this.toggleDropdown(event, button);
          break;

        case 'ArrowDown':
          event.preventDefault();
          if (!isExpanded) {
            this.openDropdown(button, dropdown);
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (!isExpanded) {
            this.openDropdown(button, dropdown);
          }
          break;
      }
    }

    handleItemKeydown(event, item) {
      const dropdown = item.closest('.dropdown-options');
      const items = Array.from(dropdown.querySelectorAll('.dropdown-item'));
      const currentIndex = items.indexOf(item);

      switch(event.key) {
        case 'Enter':
        case ' ': // Spacebar
          event.preventDefault();
          this.selectItem(event, item);
          break;

        case 'ArrowDown':
          event.preventDefault();
          this.focusNextItem(items, currentIndex, 1);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.focusNextItem(items, currentIndex, -1);
          break;

        case 'Home':
          event.preventDefault();
          this.focusItem(items[0]);
          break;

        case 'End':
          event.preventDefault();
          this.focusItem(items[items.length - 1]);
          break;

        case 'Tab':
          // Allow natural tab behavior but close dropdown
          this.closeDropdown(dropdown);
          break;
      }
    }

    focusNextItem(items, currentIndex, direction) {
      let nextIndex = currentIndex + direction;

      // Wrap around
      if (nextIndex < 0) {
        nextIndex = items.length - 1;
      } else if (nextIndex >= items.length) {
        nextIndex = 0;
      }

      this.focusItem(items[nextIndex]);
    }

    focusItem(item) {
      // Remove tabindex from all items in this dropdown
      const dropdown = item.closest('.dropdown-options');
      dropdown.querySelectorAll('.dropdown-item').forEach(i => {
        i.setAttribute('tabindex', '-1');
        i.setAttribute('aria-selected', 'false');
      });

      // Set tabindex and focus on target item
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-selected', 'true');
      item.focus();
    }

    selectItem(event, item) {
      event.stopPropagation();

      // Get data from item
      const variantId = item.dataset.variantId;
      const price = parseFloat(item.dataset.price);
      const image = item.dataset.layerImage;
      const title = item.dataset.productTitle;
      const hasVariants = item.dataset.hasVariants === 'true';
      const variantsData = item.dataset.variants;

      // Get layer ID from button
      const dropdown = item.closest('.dropdown-options');
      const button = dropdown.previousElementSibling;
      const layerId = button.dataset.layerId;

      // Update button text
      const buttonText = button.querySelector('.dropdown-button-text');
      if (buttonText) {
        buttonText.textContent = title;
      }

      // Handle variant selector
      if (hasVariants && variantsData) {
        this.showVariantSelector(layerId, variantsData, variantId);
      } else {
        this.hideVariantSelector(layerId);
        // Store selection immediately if no variants
        this.storeSelection(layerId, variantId, price, image, title);
      }

      // Close dropdown and return focus to button
      this.closeDropdown(dropdown);
      button.focus();
    }

    showVariantSelector(layerId, variantsJson, defaultVariantId) {
      // For mat layer, need to include section ID in the selector
      const selectorId = layerId === 'mat' ? `mat-${this.sectionId}` : layerId;
      const variantSelector = this.container.querySelector(`#variant-selector-${selectorId}`);
      const variantSelect = this.container.querySelector(`#variant-select-${selectorId}`);

      if (!variantSelector || !variantSelect) {
        console.log('Variant selector not found:', `#variant-selector-${selectorId}`);
        return;
      }

      try {
        const variants = JSON.parse(variantsJson);

        // Clear existing options
        variantSelect.innerHTML = '';

        // Populate with variants
        variants.forEach(variant => {
          if (variant.available) {
            const option = document.createElement('option');
            option.value = variant.id;
            option.textContent = variant.title;
            option.dataset.price = variant.price;

            // Priority order for variant images:
            // 1. variant.build_your_set_image (new variant-level metafield)
            // 2. variant.featured_image (variant-specific product image)
            // 3. null (will fall back to product-level image later)
            let variantImage = null;
            if (variant.build_your_set_image && variant.build_your_set_image.src) {
              variantImage = variant.build_your_set_image.src;
            } else if (variant.featured_image && variant.featured_image.src) {
              variantImage = variant.featured_image.src;
            }

            option.dataset.image = variantImage || '';
            if (variantImage) {
              option.dataset.variantImage = variantImage;
            }

            if (variant.id == defaultVariantId) {
              option.selected = true;
            }
            variantSelect.appendChild(option);
          }
        });

        // Show variant selector
        variantSelector.style.display = 'block';

        // Add change event listener
        variantSelect.addEventListener('change', (e) => this.onVariantChange(e, layerId));

        // Trigger initial selection
        this.onVariantChange({ target: variantSelect }, layerId);

      } catch (error) {
        console.error('Error parsing variants:', error);
        this.hideVariantSelector(layerId);
      }
    }

    hideVariantSelector(layerId) {
      // For mat layer, need to include section ID in the selector
      const selectorId = layerId === 'mat' ? `mat-${this.sectionId}` : layerId;
      const variantSelector = this.container.querySelector(`#variant-selector-${selectorId}`);
      if (variantSelector) {
        variantSelector.style.display = 'none';
      }
    }

    onVariantChange(event, layerId) {
      const select = event.target;
      const selectedOption = select.options[select.selectedIndex];

      const variantId = selectedOption.value;
      const price = parseFloat(selectedOption.dataset.price);
      // Use variant-specific image if available
      const image = selectedOption.dataset.variantImage || selectedOption.dataset.image || null;

      // Get product title from button
      const button = this.container.querySelector(`[data-layer-id="${layerId}"].dropdown-button`);
      const buttonText = button?.querySelector('.dropdown-button-text');
      const productTitle = buttonText?.textContent || '';
      const fullTitle = `${productTitle} - ${selectedOption.textContent}`;

      console.log('Variant changed:', selectedOption.textContent, 'Image:', image);

      this.storeSelection(layerId, variantId, price, image, fullTitle);
    }

    storeSelection(layerId, variantId, price, image, title) {
      // Update hidden form input (for theme's form submission)
      const hiddenInput = this.container.querySelector(`#selection-${layerId}-${this.sectionId}`);
      if (hiddenInput) {
        hiddenInput.value = variantId;
        console.log('Updated hidden input:', hiddenInput.id, '=', variantId);
      }

      // Store selection
      this.selections.set(layerId, { variantId, price, image, title });

      // Save to sessionStorage for Customizer persistence
      this.saveSelections();

      // Update canvas layer
      if (image) {
        this.updateCanvasLayer(layerId, image, title);
      }

      // Update total price
      this.updateTotalPrice();

      // Update add to cart button state
      this.updateAddToCartButton();
    }

    saveSelections() {
      const storageKey = `buildYourSet_${this.sectionId}`;
      const data = Array.from(this.selections.entries());
      sessionStorage.setItem(storageKey, JSON.stringify(data));
      console.log('Saved selections to sessionStorage:', storageKey, data);
    }

    restoreSelections() {
      const storageKey = `buildYourSet_${this.sectionId}`;
      const saved = sessionStorage.getItem(storageKey);

      if (!saved) {
        console.log('No saved selections found');
        return;
      }

      try {
        const data = JSON.parse(saved);
        console.log('Restoring selections from sessionStorage:', data);

        data.forEach(([layerId, selection]) => {
          // Find the dropdown item with this variant ID
          const dropdown = this.container.querySelector(`[data-layer-id="${layerId}"].dropdown-button`)?.nextElementSibling;
          if (!dropdown) return;

          const item = dropdown.querySelector(`[data-variant-id="${selection.variantId}"]`);
          if (!item) return;

          // Update button text
          const button = this.container.querySelector(`[data-layer-id="${layerId}"].dropdown-button`);
          const buttonText = button?.querySelector('.dropdown-button-text');
          if (buttonText) {
            buttonText.textContent = selection.title;
          }

          // Restore the selection
          this.selections.set(layerId, selection);

          // Update canvas layer
          if (selection.image) {
            this.updateCanvasLayer(layerId, selection.image, selection.title);
          }
        });

        // Update total price and button state
        this.updateTotalPrice();
        this.updateAddToCartButton();

      } catch (error) {
        console.error('Error restoring selections:', error);
      }
    }

    updateCanvasLayer(layerId, imageUrl, altText) {
      const layer = this.container.querySelector(`[data-layer-id="${layerId}"]`);
      if (!layer) return;

      // Check if image already exists
      let img = layer.querySelector('img.layer-image');

      if (img) {
        // Update existing image
        img.src = imageUrl;
        img.alt = altText;
      } else {
        // Create new image and hide placeholder
        const placeholder = layer.querySelector('.layer-placeholder');
        if (placeholder) {
          placeholder.style.display = 'none';
        }

        img = document.createElement('img');
        img.src = imageUrl;
        img.alt = altText;
        img.className = 'layer-image';
        img.loading = 'lazy';
        layer.appendChild(img);
      }
    }

    updateTotalPrice() {
      if (!this.totalPriceElement) return;

      let total = 0;
      this.selections.forEach(selection => {
        total += selection.price;
      });

      // Format as money (cents to dollars)
      const formattedTotal = '$' + (total / 100).toFixed(2);
      this.totalPriceElement.textContent = formattedTotal;
    }

    updateAddToCartButton() {
      if (!this.addToCartButton) return;

      // Get all required dropdowns
      const requiredDropdowns = this.container.querySelectorAll('.set-dropdown[data-required="true"]');

      // Check if all required selections are made
      let allRequiredSelected = true;
      requiredDropdowns.forEach(dropdown => {
        const button = dropdown.querySelector('.dropdown-button');
        const layerId = button?.dataset.layerId;

        if (!this.selections.has(layerId)) {
          allRequiredSelected = false;
        }
      });

      // Enable/disable button
      this.addToCartButton.disabled = !allRequiredSelected;
    }

    onSubmitHandler() {
      /* Uses theme's exact pattern from product-group.js */
      if (this.addToCartButton.getAttribute('aria-disabled') === 'true') return;

      if (this.selections.size === 0) {
        alert('Please select at least one product.');
        return;
      }

      this.addToCartButton.setAttribute('aria-disabled', true);
      this.addToCartButton.classList.add('loading');
      const spinner = this.addToCartButton.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();

      /* Add items to FormData */
      this.selections.forEach((selection, layerId) => {
        formData.append('items[][id]', selection.variantId);
        formData.append('items[][quantity]', 1);
      });

      const cart = document.querySelector('cart-drawer');
      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            alert(response.description || 'Error adding to cart');
            this.error = true;
            return;
          } else if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          cart.renderContents(response);
        })
        .catch((e) => {
          console.error(e);
          alert('Error adding to cart');
        })
        .finally(() => {
          this.addToCartButton.classList.remove('loading');
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          if (!this.error) this.addToCartButton.removeAttribute('aria-disabled');
          const spinner = this.addToCartButton.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        });
    }
  }

  // Initialize when DOM is ready
  function init() {
    const sections = document.querySelectorAll('.build-your-set-wrapper');
    console.log('Build Your Set: Found', sections.length, 'section(s)');
    sections.forEach(section => {
      new BuildYourSetSection(section);
    });
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinitialize when section reloads in Customizer (preserves selections via sessionStorage)
  document.addEventListener('shopify:section:load', function(event) {
    console.log('Build Your Set: Section reloaded in Customizer', event.detail.sectionId);

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      init();
    }, 100);
  });

})();
